import type {
  AccountDetailDto,
  AdminFamilyDto,
  CreateDebtInput,
  DebtDetailDto,
  DebtDto,
  AdminStatsDto,
  AdminUserDto,
  DraftInput,
  MetaDto,
  ParsedDraftDto,
  RecurringDto,
  ChangeEvent,
  DashboardDto,
  GoalDto,
  DishIdeaDto,
  HomeItemDto,
  HomeItemInput,
  HomeItemKind,
  MealPlanDto,
  PantryDto,
  InsightDto,
  MutationEnvelope,
  PriceHistoryDto,
  PricesDto,
  MutationResult,
  PocketAllowanceDto,
  PocketChildDto,
  PocketWalletDto,
  ReceiptDto,
  SnapshotDto,
  TransactionDto,
  HealthLabel,
  ShoppingSuggestionDto,
} from "@home/shared";
import { authHeader } from "./telegram.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }
}

/** Сеть недоступна (в отличие от ответа сервера с ошибкой). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("network", { cause });
    this.name = "NetworkError";
  }
}

function headers(extra: Record<string, string> = {}): Record<string, string> {
  const auth = authHeader();
  return auth ? { ...extra, authorization: auth } : extra;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers: headers(init.headers as Record<string, string>) });
  } catch (err) {
    throw new NetworkError(err);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? "err.generic");
  }
  try {
    return (await res.json()) as T;
  } catch (err) {
    // Связь оборвалась, пока читали ответ: сервер мог всё сделать — для клиента это та же «нет сети».
    throw new NetworkError(err);
  }
}

function json(method: "POST" | "PUT" | "PATCH", body: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}

export const api = {
  snapshot: () => request<SnapshotDto>("/api/snapshot"),

  mutations: (mutations: MutationEnvelope[]) =>
    request<{ results: MutationResult[] }>("/api/mutations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mutations }),
    }),

  dashboard: (month: string) => request<DashboardDto>(`/api/dashboard?month=${month}`),

  transactions: (month: string, category?: string) =>
    request<{ transactions: TransactionDto[] }>(
      `/api/transactions?month=${month}${category ? `&category=${encodeURIComponent(category)}` : ""}`,
    ),

  goals: () => request<{ goals: GoalDto[] }>("/api/goals"),

  insight: () => request<{ insight: InsightDto | null }>("/api/insights"),

  prices: () => request<PricesDto>("/api/prices"),
  priceHistory: (id: string) => request<PriceHistoryDto>(`/api/prices/${id}`),

  refreshInsight: () => request<{ insight: InsightDto }>("/api/insights", { method: "POST" }),

  meta: () => request<MetaDto>("/api/meta"),

  debts: () => request<{ debts: DebtDto[] }>("/api/debts"),
  debt: (id: string) => request<DebtDetailDto>(`/api/debts/${id}`),
  createDebt: (body: CreateDebtInput) => request<{ id: string }>("/api/debts", json("POST", body)),
  payDebt: (id: string, amount: number | null = null) => request<{ closed: boolean }>(`/api/debts/${id}/pay`, json("POST", { amount })),
  extraDebt: (id: string, amount: number, mode: "term" | "payment") =>
    request<{ closed: boolean }>(`/api/debts/${id}/extra`, json("POST", { amount, mode })),
  setFinance: (monthlyIncome: number | null, incomeDay: number | null) =>
    request<{ ok: true }>("/api/family/finance", json("PUT", { monthlyIncome, incomeDay })),
  setupDone: () => request<{ ok: true }>("/api/family/setup-done", { method: "POST" }),
  deleteDebt: (id: string) => request<{ ok: true }>(`/api/debts/${id}`, { method: "DELETE" }),

  createTransactions: (drafts: DraftInput[], source: "manual" | "text" = "manual") =>
    request<{ transactions: TransactionDto[] }>("/api/transactions", json("POST", { drafts, source })),

  deleteTransaction: (id: string) => request<{ ok: true }>(`/api/transactions/${id}`, { method: "DELETE" }),

  parse: (text: string) =>
    request<{ status: "ok" | "needs_amount" | "debt" | "unrecognized"; clarification: string | null; drafts: ParsedDraftDto[] }>(
      "/api/parse",
      json("POST", { text }),
    ),

  createGoal: (body: { name: string; target: number; currency: "UZS" | "USD"; deadline: string | null; ownerUserId: string | null }) =>
    request<{ id: string }>("/api/goals", json("POST", body)),

  contribute: (goalId: string, amount: number) =>
    request<{ saved: number; achieved: boolean }>(`/api/goals/${goalId}/contribute`, json("POST", { amount })),

  // Карманные деньги: кошелёк ребёнка (сам ребёнок — только свой), выдать и карманные по расписанию — взрослые.
  pocketChildren: () => request<{ children: PocketChildDto[] }>("/api/pocket/children"),
  pocket: (childId: string) => request<PocketWalletDto>(`/api/pocket/${childId}`),
  pocketRecord: (childId: string, kind: "gift" | "spend", amount: number, note: string | null) =>
    request<{ balance: number }>(`/api/pocket/${childId}/entries`, json("POST", { kind, amount, note })),
  pocketGive: (childId: string, amount: number, note: string | null) =>
    request<{ balance: number }>(`/api/pocket/${childId}/give`, json("POST", { amount, note })),
  pocketSave: (childId: string, goalId: string, amount: number) =>
    request<{ balance: number; saved: number; achieved: boolean }>(`/api/pocket/${childId}/save`, json("POST", { goalId, amount })),
  pocketAllowance: (childId: string, amount: number | null, weekday: number) =>
    request<{ allowance: PocketAllowanceDto | null }>(`/api/pocket/${childId}/allowance`, json("PUT", { amount, weekday })),
  deletePocketEntry: (id: string) => request<{ ok: true }>(`/api/pocket/entries/${id}`, { method: "DELETE" }),

  // Аптечка и гарантии.
  homeItems: (kind: HomeItemKind) => request<{ items: HomeItemDto[] }>(`/api/home-items?kind=${kind}`),
  createHomeItem: (body: HomeItemInput) => request<{ id: string }>("/api/home-items", json("POST", body)),
  updateHomeItem: (id: string, body: HomeItemInput) => request<{ ok: true }>(`/api/home-items/${id}`, json("PUT", body)),
  deleteHomeItem: (id: string) => request<{ ok: true }>(`/api/home-items/${id}`, { method: "DELETE" }),

  // «Холодильник», что приготовить, меню на неделю.
  pantry: () => request<PantryDto>("/api/pantry"),
  pantryAdd: (name: string) => request<{ ok: true }>("/api/pantry", json("POST", { name })),
  pantryUsed: (id: string, used: boolean) => request<{ ok: true }>(`/api/pantry/${id}`, json("PUT", { used })),
  /** Что пора докупить: закончилось и по ритму покупок. */
  pantrySuggestions: () => request<{ items: ShoppingSuggestionDto[] }>("/api/pantry/suggestions"),
  /** Своя метка семьи «полезное / обычное / вредное» для товара. */
  setProductHealth: (id: string, health: HealthLabel) => request<{ ok: true }>(`/api/products/${id}/health`, json("PUT", { health })),
  cookingIdeas: () => request<{ dishes: DishIdeaDto[] }>("/api/pantry/ideas", { method: "POST" }),
  mealPlan: () => request<{ plan: MealPlanDto | null }>("/api/meal-plan"),
  makeMealPlan: (wishes: string | null) => request<{ plan: MealPlanDto }>("/api/meal-plan", json("POST", { wishes })),

  createAccount: (body: { name: string; currency: "UZS" | "USD"; personal: boolean; stash?: boolean }) =>
    request<{ id: string }>("/api/accounts", json("POST", body)),
  account: (id: string) => request<AccountDetailDto>(`/api/accounts/${id}`),
  renameAccount: (id: string, name: string) => request<{ ok: true }>(`/api/accounts/${id}`, json("PUT", { name })),
  /** «Сверить остаток»: на счёте на самом деле столько. */
  setAccountBalance: (id: string, balance: number) => request<{ balance: number }>(`/api/accounts/${id}/balance`, json("PUT", { balance })),
  setAccountStash: (id: string, stash: boolean) => request<{ ok: true }>(`/api/accounts/${id}/stash`, json("PUT", { stash })),
  /** Правка сохранённой операции: перенести на другой счёт, «личная / общая». */
  updateTransaction: (id: string, body: { accountId?: string; scope?: "personal" | "shared" }) =>
    request<{ ok: true }>(`/api/transactions/${id}`, json("PATCH", body)),

  recurring: () => request<{ recurring: RecurringDto[] }>("/api/recurring"),

  createRecurring: (body: { name: string; amount: number; dayOfMonth: number }) =>
    request<{ id: string }>("/api/recurring", json("POST", body)),

  deleteRecurring: (id: string) => request<{ ok: true }>(`/api/recurring/${id}`, { method: "DELETE" }),
  payRecurring: (id: string) => request<{ ok: true }>(`/api/recurring/${id}/pay`, { method: "POST" }),
  skipRecurring: (id: string) => request<{ ok: true }>(`/api/recurring/${id}/skip`, { method: "POST" }),

  invite: (role: "adult" | "child") =>
    request<{ code: string; link: string | null; expiresAt: string }>("/api/family/invite", json("POST", { role })),

  setLanguage: (language: "ru" | "uz") => request<{ ok: true }>("/api/me/language", json("PUT", { language })),

  adminStats: () => request<AdminStatsDto>("/api/admin/stats"),
  adminFamilies: () => request<{ families: AdminFamilyDto[] }>("/api/admin/families"),
  adminUsers: () => request<{ users: AdminUserDto[] }>("/api/admin/users"),
  adminBlockUser: (id: string, blocked: boolean) => request<{ ok: true }>(`/api/admin/users/${id}/block`, json("POST", { blocked })),
  adminBlockFamily: (id: string, blocked: boolean) =>
    request<{ updated: number }>(`/api/admin/families/${id}/block`, json("POST", { blocked })),

  receiptFromQr: (text: string) =>
    request<ReceiptDto>("/api/receipts/qr", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ text }),
    }),

  receiptFromPhoto: (blob: Blob) => {
    const form = new FormData();
    form.append("file", blob, "receipt.jpg");
    return request<ReceiptDto>("/api/receipts/photo", { method: "POST", body: form });
  },

  matchReceipt: (id: string, listId: string) =>
    request<ReceiptDto>(`/api/receipts/${id}/match`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ listId }),
    }),

  saveReceipt: (id: string, markBought: boolean) =>
    request<ReceiptDto>(`/api/receipts/${id}/save`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ markBought }),
    }),

  uploadPhoto: (blob: Blob) => {
    const form = new FormData();
    form.append("file", blob, "photo.jpg");
    return request<{ id: string }>("/api/files", { method: "POST", body: form });
  },
};

export function fileUrl(fileId: string, token: string): string {
  return `/api/files/${fileId}?t=${encodeURIComponent(token)}`;
}

export interface EventStreamHandlers {
  onChange: (event: ChangeEvent) => void;
  onReady: () => void;
  /** Любые данные из потока, включая пинги, — для сторожевого таймера. */
  onActivity?: () => void;
  /** Деньги семьи изменились (в боте, в приложении, у другого члена семьи). */
  onFinance?: () => void;
}

/**
 * Подписка на SSE через fetch (EventSource не умеет слать заголовок авторизации).
 * Промис завершается, когда поток закрыт; ошибки сети — через reject.
 */
export async function streamEvents(since: string, handlers: EventStreamHandlers, signal: AbortSignal): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/events?since=${encodeURIComponent(since)}`, {
      headers: headers({ accept: "text/event-stream" }),
      signal,
    });
  } catch (err) {
    throw new NetworkError(err);
  }
  if (!res.ok || !res.body) throw new ApiError(res.status, "events");

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    handlers.onActivity?.();
    buffer += value;
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseSseBlock(block);
      if (parsed?.event === "change") handlers.onChange(JSON.parse(parsed.data) as ChangeEvent);
      else if (parsed?.event === "ready") handlers.onReady();
      else if (parsed?.event === "finance") handlers.onFinance?.();
    }
  }
}

export function parseSseBlock(block: string): { event: string; data: string; id?: string } | null {
  let event = "message";
  let id: string | undefined;
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    const value = idx === -1 ? "" : line.slice(idx + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    else if (field === "id") id = value;
  }
  return data.length ? { event, data: data.join("\n"), id } : null;
}
